import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Grid, TextField, MenuItem,
  CircularProgress, Chip, Divider, FormControlLabel, Switch, Alert
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { compareStandingOrders } from '../services/api';

const THEME = '#5c6bc0';

// Tokenise into words + whitespace runs so we can rebuild readable text.
function tokenize(text) {
  if (!text) return [];
  return text.match(/\S+|\s+/g) || [];
}

// Longest Common Subsequence over token arrays -> a list of
// { type: 'equal'|'add'|'remove', value } chunks. No external dependency.
function diffTokens(aTokens, bTokens) {
  const n = aTokens.length;
  const m = bTokens.length;
  // DP table of LCS lengths.
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (aTokens[i] === bTokens[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  const push = (type, value) => {
    const last = out[out.length - 1];
    if (last && last.type === type) {
      last.value += value;
    } else {
      out.push({ type, value });
    }
  };
  while (i < n && j < m) {
    if (aTokens[i] === bTokens[j]) {
      push('equal', aTokens[i]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push('remove', aTokens[i]);
      i++;
    } else {
      push('add', bTokens[j]);
      j++;
    }
  }
  while (i < n) { push('remove', aTokens[i]); i++; }
  while (j < m) { push('add', bTokens[j]); j++; }
  return out;
}

function DiffView({ left, right, inlineMode }) {
  const chunks = diffTokens(tokenize(left.content), tokenize(right.content));

  if (inlineMode) {
    return (
      <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="caption" color="text.secondary">
          <Box component="span" sx={{ bgcolor: '#ffebee', px: 0.5 }}>removed (left only)</Box>
          {'  '}
          <Box component="span" sx={{ bgcolor: '#e8f5e9', px: 0.5 }}>added (right only)</Box>
        </Typography>
        <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>
          {chunks.map((c, idx) => {
            if (c.type === 'equal') return <span key={idx}>{c.value}</span>;
            if (c.type === 'remove') {
              return (
                <Box key={idx} component="span"
                  sx={{ bgcolor: '#ffebee', color: '#b71c1c', textDecoration: 'line-through' }}>
                  {c.value}
                </Box>
              );
            }
            return (
              <Box key={idx} component="span" sx={{ bgcolor: '#e8f5e9', color: '#1b5e20' }}>
                {c.value}
              </Box>
            );
          })}
        </Typography>
      </Paper>
    );
  }

  // Side-by-side: left shows equal + removed, right shows equal + added.
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip label={`SO ${left.so_number}`} size="small" sx={{ bgcolor: THEME, color: 'white' }} />
            <Chip label={left.chamber} size="small" variant="outlined" />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>{left.title}</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>
            {chunks.filter(c => c.type !== 'add').map((c, idx) => (
              c.type === 'remove'
                ? <Box key={idx} component="span" sx={{ bgcolor: '#ffebee', color: '#b71c1c' }}>{c.value}</Box>
                : <span key={idx}>{c.value}</span>
            ))}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip label={`SO ${right.so_number}`} size="small" sx={{ bgcolor: THEME, color: 'white' }} />
            <Chip label={right.chamber} size="small" variant="outlined" />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>{right.title}</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>
            {chunks.filter(c => c.type !== 'remove').map((c, idx) => (
              c.type === 'add'
                ? <Box key={idx} component="span" sx={{ bgcolor: '#e8f5e9', color: '#1b5e20' }}>{c.value}</Box>
                : <span key={idx}>{c.value}</span>
            ))}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default function StandingOrdersCompare() {
  const [leftSo, setLeftSo] = useState('1');
  const [leftChamber, setLeftChamber] = useState('hor');
  const [rightSo, setRightSo] = useState('2');
  const [rightChamber, setRightChamber] = useState('hor');
  const [inlineMode, setInlineMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleCompare = async () => {
    if (!leftSo.trim() || !rightSo.trim()) {
      setError('Please enter a Standing Order number for both sides.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await compareStandingOrders(
        leftSo.trim(), rightSo.trim(), leftChamber, rightChamber
      );
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || err.message || 'Failed to compare. Please try again.');
      setResult(null);
    }
    setLoading(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CompareArrowsIcon sx={{ color: THEME, fontSize: 32 }} />
        <Typography variant="h4">Compare Standing Orders</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Compare any two Standing Orders side by side with a word-level diff &mdash;
        including across the two chambers. Changes added on the right are highlighted
        green; text only on the left is highlighted red.
      </Typography>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        This compares two distinct Standing Orders. Comparing successive
        <em> versions over time</em> (track-changes between editions) is not yet
        available &mdash; the data set currently holds only the single current
        edition of each Standing Order.
      </Alert>

      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth label="Left SO number" value={leftSo}
              onChange={(e) => setLeftSo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCompare()}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select fullWidth label="Left chamber" value={leftChamber}
              onChange={(e) => setLeftChamber(e.target.value)}
            >
              <MenuItem value="hor">House of Representatives</MenuItem>
              <MenuItem value="senate">Senate</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth label="Right SO number" value={rightSo}
              onChange={(e) => setRightSo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCompare()}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select fullWidth label="Right chamber" value={rightChamber}
              onChange={(e) => setRightChamber(e.target.value)}
            >
              <MenuItem value="hor">House of Representatives</MenuItem>
              <MenuItem value="senate">Senate</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button
              variant="contained" onClick={handleCompare} disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CompareArrowsIcon />}
              sx={{ borderRadius: 3, px: 4, bgcolor: THEME, '&:hover': { bgcolor: '#3f51b5' } }}
            >
              Compare
            </Button>
          </Grid>
          <Grid item xs={12} md="auto">
            <FormControlLabel
              control={<Switch checked={inlineMode} onChange={(e) => setInlineMode(e.target.checked)} />}
              label="Inline diff"
            />
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
      )}

      {result && !error && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <GavelIcon sx={{ color: THEME }} />
            <Typography variant="h6">
              SO {result.left.so_number} ({result.left.chamber}) vs SO {result.right.so_number} ({result.right.chamber})
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <DiffView left={result.left} right={result.right} inlineMode={inlineMode} />
        </Box>
      )}
    </Box>
  );
}
